import React, { useContext, useCallback } from 'react';

import CountryFlag from 'components/base/CountryFlag/index';
import { TopTerritoriesItem } from './TopTerritories';
import { EntityContext } from 'context/entityContext';
import { IEntity, ITopTerritoriesEntityCountryItem } from './types';
import getShortName from 'helpers/getShortName';
import TooltipWrapper from 'components/base/TooltipWrapper';
import { MAX_COUNTRY_NAME } from 'config';

/**
 * TopCountry displays a single TopCountry list item in the
 * SpinsTopTerritories component.
 * @param {object} props React props
 * @param {object} props.item Country item
 * @param {('country'|'entity')} props.compareMode The current compare mode
 * @param {object} props.entity Entity object like { id, type }
 * @param {number} props.rank This countries' rank
 */
interface IProps {
  item: ITopTerritoriesEntityCountryItem;
  compareMode: string;
  entity: IEntity;
  rank: number;
  isMobileView?: boolean;
  isSmaller?: boolean;
  isStreaming?: boolean;
  onClick?: Function;
}

export const TopCountry: React.FC<IProps> = (props) => {
  const {
    item,
    compareMode,
    entity,
    rank,
    isMobileView,
    isSmaller,
    isStreaming,
    onClick,
  } = props;

  const ctx = useContext(EntityContext);
  const { filters, countryIds, dataColors, entityIds } = ctx;

  const getFontWeight = useCallback(
    (countryId) => {
      if (countryId === filters?.country) return 600;
      if (compareMode === 'country' && countryIds?.includes(countryId)) {
        return 600;
      }

      return 400;
    },
    [compareMode, countryIds, filters]
  );

  const getColor = useCallback(
    (countryId) => {
      if (countryIds && compareMode === 'country') {
        const idx = countryIds.findIndex((el) => countryId === el);
        return dataColors[idx];
      }

      if (compareMode === 'entity') {
        const idx = entityIds.findIndex((el) => entity.id === el);
        return dataColors[idx];
      }

      return;
    },
    [compareMode, countryIds, dataColors, entityIds, entity]
  );
  const maxCountryName = isStreaming ? 24 : MAX_COUNTRY_NAME;
  const name = item?.metadata?.displayName || item?.countryName;
  const countryName = getShortName(name, maxCountryName);
  const emptyFunction = (): void => undefined;

  const id = item?.id || item?.countryId;

  return (
    <TopTerritoriesItem
      data-rank={rank}
      fontWeight={getFontWeight(id)}
      dataColor={getColor(id)}
      isMobile={isMobileView}
      isSmaller={isSmaller}
      onClick={compareMode ? emptyFunction : onClick}
      showCursor={!compareMode}
      isStreaming={isStreaming}
    >
      {compareMode ? (
        <>
          {item.metadata && <CountryFlag {...item.metadata} small />}
          {id}
        </>
      ) : (
        <TooltipWrapper
          condition={name && name?.length > maxCountryName}
          tooltipContent={name}
        >
          {item.metadata && <CountryFlag {...item.metadata} small />}
          {countryName}
        </TooltipWrapper>
      )}
    </TopTerritoriesItem>
  );
};
